import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ShowToastOptions, ToastQueueItem } from './types';

/** No module-level counter — avoids SSR/HMR/test cross-talk and monotonic ID assumptions. */
function createToastId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c && typeof c.randomUUID === 'function') {
    return `cds-toast-${c.randomUUID()}`;
  }
  const timestamp =
    typeof performance !== 'undefined' && performance.now
      ? performance.now().toString(36)
      : Date.now().toString(36);
  const r1 = Math.random().toString(36).slice(2, 11);
  const r2 = Math.random().toString(36).slice(2, 11);
  return `cds-toast-${timestamp}-${r1}${r2}`;
}

export type ToastContextValue = {
  show: (options: ShowToastOptions) => string;
  dismiss: (id: string) => void;
  toasts: ToastQueueItem[];
};

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Enqueue and dismiss toasts from under a mounted `ToastProvider`.
 *
 * @returns `show` — adds a toast and returns its id; `dismiss` — remove by id; `toasts` — current queue.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

export function useToastState() {
  const [toasts, setToasts] = useState<ToastQueueItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((options: ShowToastOptions) => {
    const id = createToastId();
    const item: ToastQueueItem = {
      id,
      title: options.title,
      message: options.message,
      durationMs: options.durationMs ?? 4000,
      prefixSlot: options.prefixSlot,
      suffixSlot: options.suffixSlot,
      role: options.role,
    };
    setToasts((prev) => [...prev, item]);
    return id;
  }, []);

  const value = useMemo(() => ({ show, dismiss, toasts }), [
    show,
    dismiss,
    toasts,
  ]);

  return { value, toasts, dismiss };
}

export { ToastContext };
